import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import SEO from "../../components/seo";
import SiteLayout from "../../components/site/SiteLayout";
import Card from "../../components/site/Card";
import api, { url as apiUrl } from "../../api";
import { mapPredio } from "../../lib/predio";
import { useInicio } from "../../lib/inicio";
import { AuthContext } from "../../context/GlobalContextProvider";

// Quantos cartões-fantasma mostrar enquanto a API não responde. Bate com a
// grade de 4 colunas do desktop.
const SKELETON_COUNT = 4;

export default function Empreendimentos() {
  const context = useContext(AuthContext);
  const site = useInicio();
  const [rawList, setRawList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cidade, setCidade] = useState("");
  const [status, setStatus] = useState("");
  const [dorm, setDorm] = useState("");
  const [finalidade, setFinalidade] = useState("");

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    try {
      const response = await api.get("/predios?_sort=ordem");
      setRawList(response.data);
    } catch (error) {
      // A grade cai no estado vazio; o erro não deve travar o esqueleto.
      setRawList([]);
    } finally {
      setLoading(false);
    }
  };

  const predios = useMemo(
    () => rawList.map((raw) => mapPredio(raw, apiUrl)),
    [rawList]
  );

  const filtered = predios.filter(
    (p) =>
      (!cidade || p.cidadeFilter === cidade) &&
      (!status || p.statusFilter === status) &&
      (!dorm || p.dormValues.includes(dorm)) &&
      (!finalidade || p.finalidadeFilter === finalidade)
  );

  const toggleDorm = (value) => {
    setDorm((current) => (current === value ? "" : value));
  };

  const sortBuildings = async (sorted) => {
    sorted.forEach(async (building) => {
      await api({
        method: "PUT",
        url: `/predios/${building.id}`,
        data: { ...building, ordem: building.ordem },
        headers: { Authorization: `Bearer ${context?.token}` },
      });
    });
  };

  const handleSort = () => {
    const list = [...rawList];
    const draggedItem = list.splice(dragItem.current, 1)[0];
    list.splice(dragOverItem.current, 0, draggedItem);
    dragItem.current = null;
    dragOverItem.current = null;

    const withOrder = list.map((f, index) => ({ ...f, ordem: index }));
    setRawList(withOrder);
    sortBuildings(withOrder);
  };

  return (
    <SiteLayout>
      <SEO
        lang="pt-BR"
        keywords={[`Bivver`, `Empreendimentos`, `imobiliários`, `praia`, `prédios`]}
        description="Empreendimentos Bivver em Balneário Piçarras e Camboriú/SC: lançamentos e últimas unidades à venda perto da praia."
        title="Empreendimentos Bivver — Litoral de Santa Catarina"
      />

      <div className="filterbar-wrap">
        <h1>{site.empreendimentosTitulo}</h1>
        <p>{site.empreendimentosSubtitulo}</p>

        <div className="filterbar">
          <div className="filter-field">
            <label>{site.filtroCidadeLabel}</label>
            <select
              id="filtro-cidade"
              value={cidade}
              onChange={(e) => setCidade(e.target.value)}
            >
              <option value="">Todas as cidades</option>
              <option value="balneario-picarras">Balneário Piçarras</option>
              <option value="camboriu">Camboriú</option>
              <option value="barra-velha">Barra Velha</option>
              <option value="penha">Penha</option>
            </select>
          </div>
          <div className="filter-field">
            <label>{site.filtroStatusLabel}</label>
            <select
              id="filtro-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">Todos os status</option>
              <option value="pre-lancamento">Pré-Lançamento</option>
              <option value="lancamento">Lançamento</option>
              <option value="ultimas-unidades">Últimas Unidades</option>
              <option value="entregue">Entregue</option>
            </select>
          </div>
          <div className="filter-field">
            <label>{site.filtroDormitoriosLabel}</label>
            <div className="pillgroup" style={{ display: "flex", gap: "6px" }}>
              {["1", "2", "3"].map((v) => (
                <button
                  key={v}
                  type="button"
                  data-value={v}
                  className={dorm === v ? "on" : ""}
                  onClick={() => toggleDorm(v)}
                  style={{
                    flex: 1,
                    padding: "9px 4px",
                    border: "1.5px solid var(--line)",
                    borderRadius: "9px",
                    background: "#fff",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div className="filter-field">
            <label>{site.filtroFinalidadeLabel}</label>
            <select
              id="filtro-finalidade"
              value={finalidade}
              onChange={(e) => setFinalidade(e.target.value)}
            >
              <option value="">Investimento ou moradia</option>
              <option value="investimento">Investimento</option>
              <option value="moradia">Moradia</option>
            </select>
          </div>
          <button className="filter-cta" id="filtro-btn" type="button">
            {site.filtroBotao}
          </button>
        </div>
      </div>

      <div className="content" id="grade">
        <div className="section-block">
          <div className="carousel" aria-busy={loading}>
            {loading &&
              Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                <div
                  className="card-skeleton"
                  // eslint-disable-next-line react/no-array-index-key
                  key={`skeleton-${index}`}
                  aria-hidden="true"
                />
              ))}
            {!loading &&
              filtered.map((predio, index) =>
                context?.user ? (
                  <div
                    key={predio.id}
                    draggable
                    onDragStart={() => {
                      dragItem.current = index;
                    }}
                    onDragEnter={() => {
                      dragOverItem.current = index;
                    }}
                    onDragEnd={handleSort}
                    onDragOver={(e) => e.preventDefault()}
                    style={{ cursor: "move" }}
                  >
                    <Card predio={predio} />
                  </div>
                ) : (
                  <Card key={predio.id} predio={predio} />
                )
              )}
          </div>
          <p
            className="empty-state"
            id="empty-state"
            style={{ display: !loading && filtered.length === 0 ? "block" : "none" }}
          >
            {site.empreendimentosVazio}
          </p>
        </div>
      </div>
    </SiteLayout>
  );
}
